import React, { createContext, useContext, useRef, useState, useEffect } from 'react';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const AuthContext = createContext(null);

export function useAuth() {
  return useContext(AuthContext);
}

function resolveTenantSlugSync() {
  const fromQuery = (new URLSearchParams(window.location.search).get('tenant') || '').toLowerCase();
  if (fromQuery) return fromQuery;
  return (window.localStorage?.getItem('tenantSlug') || '').toLowerCase();
}

async function resolveTenantSlug() {
  const sync = resolveTenantSlugSync();
  if (sync) return sync;
  try {
    const { data } = await axios.get(`${API_URL}/api/platform/config`);
    const cfg = data || {};
    const host = window.location.hostname.toLowerCase();
    const baseDomain = (cfg.app_base_domain || '').toLowerCase();
    if (baseDomain && host.endsWith(`.${baseDomain}`) && host !== baseDomain) {
      return host.slice(0, host.length - baseDomain.length - 1);
    }
    return (cfg.default_tenant_slug || '').toLowerCase();
  } catch {
    return '';
  }
}

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const loggingOut = useRef(false);

  useEffect(() => {
    // Don't probe tenant-user auth on the super-admin console, the public
    // signup flow, or the public clinic landing / booking pages — those
    // are anonymous-first surfaces with their own auth realm (or none).
    const p = window.location.pathname;
    if (
      p.startsWith('/superadmin') ||
      p.startsWith('/signup') ||
      p.startsWith('/register') ||
      p.startsWith('/c/') ||
      p.startsWith('/book/') ||
      p.startsWith('/r/') ||
      p === '/'
    ) {
      setLoading(false);
      return;
    }
    const checkAuth = async () => {
      try {
        const { data } = await axios.get(`${API_URL}/api/auth/me`, { withCredentials: true });
        setUser(data);
      } catch {
        setUser(null);
      }
      setLoading(false);
    };
    checkAuth();
  }, []);

  const logout = async () => {
    if (loggingOut.current) return;
    loggingOut.current = true;
    // Drop local session immediately so UI cannot bounce back as signed-in.
    setUser(null);
    setLoading(false);
    try {
      await axios.post(`${API_URL}/api/auth/logout`, {}, { withCredentials: true });
    } catch {
      /* cookie clear may still have applied; continue to login */
    }
    // Remember clinic via localStorage / platform default — never put
    // ?tenant= back in the address bar after logout.
    const slug = await resolveTenantSlug();
    if (slug) {
      try { window.localStorage.setItem('tenantSlug', slug); } catch { /* noop */ }
    }
    window.location.replace('/login');
  };

  return (
    <AuthContext.Provider value={{ user, setUser, logout, loading }}>
      {children}
    </AuthContext.Provider>
  );
}
